import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const ServicesContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

const normalizeService = (raw = {}) => ({
  id: raw.id,
  name: raw.name || "",
  category: raw.category || "",
  categoryOrder: Number(raw.categoryOrder || 0),
  basePrice: Number(raw.basePrice || 0),
  defaultDiscount: Number(raw.defaultDiscount || 0),
  finalPrice: Number(raw.finalPrice || 0),
  active: Boolean(raw.active),
  order: Number(raw.order || 0),
  scope: raw.scope || "tooth",
  meta: raw.meta || null,
  createdAt: raw.createdAt || null,
  updatedAt: raw.updatedAt || null,
});

const sortServices = (list = []) =>
  [...list].sort((a, b) => {
    if (a.categoryOrder !== b.categoryOrder) return a.categoryOrder - b.categoryOrder;
    if (a.category !== b.category) return a.category.localeCompare(b.category);
    if (a.order !== b.order) return a.order - b.order;
    return a.name.localeCompare(b.name);
  });

export function ServicesProvider({ children }) {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchServices = useCallback(async () => {
    if (!token) {
      setServices([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/services`, {
        headers: {
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-au putut încărca serviciile");
      }
      const list = data?.data?.services || [];
      setServices(sortServices(list.map(normalizeService)));
    } catch (err) {
      console.error("Eroare fetch servicii:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const createService = useCallback(
    async (payload) => {
      if (!token) return null;
      const res = await fetch(`${API_URL}/api/services`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut crea serviciul");
      const service = normalizeService(data?.data?.service);
      setServices((prev) => sortServices([service, ...prev]));
      return service;
    },
    [authHeaders, token]
  );

  const updateService = useCallback(
    async (payload) => {
      if (!payload?.id || !token) return null;
      const res = await fetch(`${API_URL}/api/services/${payload.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut actualiza serviciul");
      const service = normalizeService(data?.data?.service);
      setServices((prev) => sortServices(prev.map((s) => (s.id === service.id ? service : s))));
      return service;
    },
    [authHeaders, token]
  );

  const deleteService = useCallback(
    async (serviceId) => {
      if (!serviceId || !token) return false;
      const res = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: "DELETE",
        headers: {
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut șterge serviciul");
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
      return true;
    },
    [authHeaders, token]
  );

  const reorderServices = useCallback(
    async (updates) => {
      if (!token || !Array.isArray(updates) || updates.length === 0) return false;
      const res = await fetch(`${API_URL}/api/services/reorder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ updates }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Nu s-a putut rearanja lista");
      setServices((prev) => {
        const map = new Map(prev.map((s) => [s.id, s]));
        updates.forEach((u) => {
          const existing = map.get(u.id);
          if (!existing) return;
          map.set(u.id, {
            ...existing,
            order: typeof u.order !== "undefined" ? u.order : existing.order,
            categoryOrder:
              typeof u.categoryOrder !== "undefined" ? u.categoryOrder : existing.categoryOrder,
          });
        });
        return sortServices(Array.from(map.values()));
      });
      return true;
    },
    [authHeaders, token]
  );

  const value = useMemo(
    () => ({
      services,
      loading,
      error,
      fetchServices,
      createService,
      updateService,
      deleteService,
      reorderServices,
    }),
    [services, loading, error, fetchServices, createService, updateService, deleteService, reorderServices]
  );

  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export const useServices = () => useContext(ServicesContext);
